import { Stack } from '@mui/material';
import { deleteBlacklist, getAllBlacklists, updateBlacklistNew } from 'API/company-blacklist';
import { getCoPilotRouteForPathAndTab } from 'features/co-pilot/co-pilot-routes';
import { ZoomLevels } from 'enums';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiRoundTextField from 'features/ui/base-components/pai-round-text-field';
import { useDebounce } from 'hooks/use-debounce';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import useErrorStore from 'stores/error-store';
import useAppBarNavigationStore, { NavigationStates } from 'stores/app-bar-navigation-store';
import { makeStyles } from 'tss-react/mui';
import { BlacklistUploadVariant } from '../campaign-builder/campaign-builder.types';
import { useLinkedinAccountsQuery } from '../linkedin-accounts/hooks';
import AddBlacklistDialog from './add-blacklist-dialog';
import { useAddBlacklist } from './use-add-blacklist';
import { Blacklist_New, BlacklistTypes } from './company-blacklist.types';
import DeleteBlacklistDialog from './delete-blacklist-dialog';
import EditBlacklistNameDialog from './edit-blacklist-name-dialog';
import GlobalBlacklists from './global-blacklists';

const GlobalBlacklistOverview: React.FC = () => {
  const { classes } = useStyles();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const setActiveTab = useAppBarNavigationStore((state) => state.setActiveTab);

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [blacklists, setBlacklists] = useState<Blacklist_New[]>([]);
  const [blacklistsLoading, setBlacklistsLoading] = useState(false);
  const [linkedinAccounts, setLinkedinAccounts] = useState<LinkedinAccount[]>([]);
  const {
    data: queriedLinkedinAccounts = [],
    error: linkedinAccountsError,
  } = useLinkedinAccountsQuery();

  /**
   * Der Anlage-Dialog liegt jetzt in `useAddBlacklist` — damit der Chat DENSELBEN
   * Dialog oeffnen kann (Karte `open_dialog`). Ein Dialog, zwei Aufrufwege.
   */
  const blacklistDialog = useAddBlacklist({
    linkedinAccounts,
    sprache: i18n.languages[0],
    onCreated: async () => {
      await fetchBlacklists(true);
    },
    onError: (error) => handleOpenErrorDialog(error as never, '/blacklists', 'post'),
    onNeedsRelogin: () => {
      const shouldOpenAccountSync = window.confirm(
        t('Your LinkedIn account session is not active. Do you want to open LinkedIn Account Management and login again?')
      );
      if (!shouldOpenAccountSync) return;
      const route = getCoPilotRouteForPathAndTab(location.pathname, NavigationStates.LINKEDIN_ACCOUNT_MANAGEMENT);
      if (route) navigate(route);
      else setActiveTab(NavigationStates.LINKEDIN_ACCOUNT_MANAGEMENT, false);
    },
  });
  const addBlacklistDialog = blacklistDialog.state;
  const setAddBlacklistDialog = blacklistDialog.setState;
  const { handleOpenAddBlacklistDialog, handleCloseAddBlacklistDialog, handleCreateGlobalBlacklist } = {
    handleOpenAddBlacklistDialog: blacklistDialog.open,
    handleCloseAddBlacklistDialog: blacklistDialog.close,
    handleCreateGlobalBlacklist: blacklistDialog.dialogProps.handleCreate,
  };
  const d = blacklistDialog.dialogProps;

  const [deleteBlacklistDialog, setDeleteBlacklistDialog] = useState<{ open: boolean; loading: boolean; blacklist: Blacklist_New | null }>({
    open: false,
    loading: false,
    blacklist: null,
  });
  const [editBlacklistNameDialog, setEditBlacklistNameDialog] = useState<{
    open: boolean;
    loading: boolean;
    blacklist: Blacklist_New | null;
    blacklistName: string;
  }>({
    open: false,
    loading: false,
    blacklist: null,
    blacklistName: '',
  });
  const [removeBlacklistItemsLoading, setRemoveBlacklistItemsLoading] = useState<boolean>(false);
  const [searchParam, setSearchParam] = useState<string>('');
  const debouncedSearch = useDebounce(searchParam, 300);

  const fetchBlacklists = async (preventLoading?: boolean) => {
    if (!preventLoading) {
      setBlacklistsLoading(true);
    }

    const res = await getAllBlacklists();

    if (!res.error) {
      if (!_.isNil(res.response)) {
        const resBlacklists: Blacklist_New[] = res.response;
        setBlacklists(resBlacklists);
      } else {
        setBlacklists([]);
      }
    } else {
      handleOpenErrorDialog(res.error, '/blacklists', 'get');
    }

    if (!preventLoading) {
      setBlacklistsLoading(false);
    }
  };

  useEffect(() => {
    fetchBlacklists();
  }, []);

  useEffect(() => {
    setLinkedinAccounts(queriedLinkedinAccounts);
    const defaultAccountId = queriedLinkedinAccounts[0]?._id ?? '';
    setAddBlacklistDialog((prev) => ({
      ...prev,
      selectedLinkedinAccount: prev.selectedLinkedinAccount || defaultAccountId,
    }));
  }, [queriedLinkedinAccounts]);

  useEffect(() => {
    if (!_.isNil(linkedinAccountsError)) {
      handleOpenErrorDialog(linkedinAccountsError, '/linkedinAccounts/getAllLinkedInAccounts', 'get');
    }
  }, [linkedinAccountsError, handleOpenErrorDialog]);

  /**
   * Add local blacklist dialog handlers
   */

  /**
   * Edit blacklist items dialog handlers
   */

  const handleOpenEditBlacklistItemsDialog = (blacklist: Blacklist_New) => {
    return () => {
      setAddBlacklistDialog((prev) => ({
        ...prev,
        open: true,
        blacklist,
        blacklistType: blacklist.blacklistType,
        selectedLinkedinAccount: prev.selectedLinkedinAccount || linkedinAccounts[0]?._id || '',
      }));
    };
  };

  const handleUpdateBlacklistItems = async () => {
    const { blacklist, blacklistType, blacklistUploadVariant, individualItemsList, fileItemsList, selectedCompanies } = addBlacklistDialog;

    if (_.isNil(blacklist)) return;

    setAddBlacklistDialog((prev) => ({ ...prev, loading: true }));

    const newBlacklistItems =
      blacklistUploadVariant === BlacklistUploadVariant.INDIVIDUAL
        ? individualItemsList.map((item) => {
            if (blacklistType === BlacklistTypes.CONTACT) {
              return { contactUrl: item };
            } else {
              return { companyName: item, companyUrn: '' };
            }
          })
        : blacklistUploadVariant === BlacklistUploadVariant.FILE
        ? fileItemsList.map((item) => {
            if (blacklistType === BlacklistTypes.CONTACT) {
              return { contactUrl: item.url };
            } else {
              return { companyName: item.company, companyUrn: '' };
            }
          })
        : blacklistUploadVariant === BlacklistUploadVariant.LINKEDIN_COMPANY_PICKER
        ? selectedCompanies.map((item) => ({
            companyName: item[1],
            companyUrn: item[0],
          }))
        : [];

    const res = await updateBlacklistNew(blacklist._id, { items: newBlacklistItems, action: 'add' });

    await fetchBlacklists(true);

    if (!res.error) {
      handleCloseAddBlacklistDialog();
    } else {
      setAddBlacklistDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/blacklists/:blacklistId', 'put');
    }
  };

  /**
   * Edit blacklist name dialog handlers
   */

  const handleOpenEditBlacklistNameDialog = (blacklist: Blacklist_New) => {
    return () => {
      setEditBlacklistNameDialog((prev) => ({ ...prev, open: true, blacklist, blacklistName: blacklist.name ?? '' }));
    };
  };

  const handleCloseEditBlacklistNameDialog = () => {
    setEditBlacklistNameDialog({
      open: false,
      loading: false,
      blacklist: null,
      blacklistName: '',
    });
  };

  const handleEditBlacklistNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditBlacklistNameDialog((prev) => ({ ...prev, blacklistName: event.target.value }));
  };

  const handleRenameBlacklist = async () => {
    const { blacklist, blacklistName } = editBlacklistNameDialog;

    if (_.isNil(blacklist)) return;

    setEditBlacklistNameDialog((prev) => ({ ...prev, loading: true }));

    const res = await updateBlacklistNew(blacklist._id, { name: blacklistName });

    if (!res.error) {
      setBlacklists((prev) => {
        const updateBlacklists = [...prev];

        const findBlacklist = updateBlacklists.find((list) => list._id === blacklist._id);

        if (!_.isNil(findBlacklist)) {
          findBlacklist.name = blacklistName;
        }

        return updateBlacklists;
      });
      handleCloseEditBlacklistNameDialog();
    } else {
      setEditBlacklistNameDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/blacklists/:blacklistId', 'put');
    }
  };

  /**
   * Delete blacklist dialog handlers
   */

  const handleOpenDeleteBlacklistDialog = (blacklist: Blacklist_New) => {
    return () => {
      setDeleteBlacklistDialog((prev) => ({ ...prev, open: true, blacklist }));
    };
  };

  const handleCloseDeleteBlacklistDialog = () => {
    setDeleteBlacklistDialog({ open: false, loading: false, blacklist: null });
  };

  const handleDeleteBlacklist = async () => {
    const { blacklist } = deleteBlacklistDialog;

    if (_.isNil(blacklist)) return;

    setDeleteBlacklistDialog((prev) => ({ ...prev, loading: true }));

    const res = await deleteBlacklist(blacklist._id);

    if (!res.error) {
      fetchBlacklists();
      handleCloseDeleteBlacklistDialog();
    } else {
      setDeleteBlacklistDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/blacklists/:blacklistId', 'delete');
    }
  };

  /**
   * Blacklist items preview handlers
   */

  const handleRemoveSingleBlacklistItem = (blacklist: Blacklist_New) => {
    return (identifier: string) => {
      return async () => {
        const newBlacklistItems = _.cloneDeep(blacklist.items ?? []);
        const deletedItems = newBlacklistItems.filter((item) => item.companyName == identifier || item.contactUrl === identifier);

        setRemoveBlacklistItemsLoading(true);

        const res = await updateBlacklistNew(blacklist._id, { items: deletedItems, action: 'remove' });

        if (!res.error) {
          setBlacklists((prev) => {
            const updateBlacklists = [...prev];

            const findBlacklist = updateBlacklists.find((list) => list._id === blacklist._id);

            if (!_.isNil(findBlacklist) && !_.isNil(findBlacklist.items)) {
              _.remove(findBlacklist.items, (item) => item.companyName == identifier || item.contactUrl === identifier);
            }

            return updateBlacklists;
          });
        } else {
          handleOpenErrorDialog(res?.error, '/blacklists/:blacklistId', 'put');
        }

        setRemoveBlacklistItemsLoading(false);
      };
    };
  };

  /**
   * Search handlers
   */

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchParam(value);
  };

  const handleSearchClear = () => {
    setSearchParam('');
  };

  const filteredBlacklists = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return blacklists;

    return blacklists.filter((list) => {
      const nameMatch = (list.name || '').toLowerCase().includes(q);
      return nameMatch;
    });
  }, [blacklists, debouncedSearch]);

  return (
    <>
      <div className={classes.container}>
        <Stack className={classes.tabContainer} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
          <PaiRoundTextField
            placeholder={t('search')}
            value={searchParam}
            variant={'search'}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
            style={{ width: '350px' }}
          />
          <PaiButton text={t('createNewBlacklist')} size={'medium'} border={'rounded'} action={handleOpenAddBlacklistDialog} />
        </Stack>
        <div className={classes.content}>
          {blacklistsLoading ? (
            <PaiDotLoader />
          ) : (
            <GlobalBlacklists
              globalBlacklists={filteredBlacklists}
              removeBlacklistItemsLoading={removeBlacklistItemsLoading}
              handleOpenAddBlacklistDialog={handleOpenAddBlacklistDialog}
              handleOpenEditBlacklistItemsDialog={handleOpenEditBlacklistItemsDialog}
              handleOpenEditBlacklistNameDialog={handleOpenEditBlacklistNameDialog}
              handleOpenDeleteBlacklistDialog={handleOpenDeleteBlacklistDialog}
              handleRemoveSingleBlacklistItem={handleRemoveSingleBlacklistItem}
            />
          )}
        </div>
      </div>

      <AddBlacklistDialog
        open={addBlacklistDialog.open}
        loading={addBlacklistDialog.loading}
        blacklist={addBlacklistDialog.blacklist}
        blacklistName={addBlacklistDialog.blacklistName}
        blacklistType={addBlacklistDialog.blacklistType}
        blacklistUploadVariant={addBlacklistDialog.blacklistUploadVariant}
        individualItemsTextField={addBlacklistDialog.individualItemsTextField}
        individualItemsTextFieldError={addBlacklistDialog.individualItemsTextFieldError}
        individualItemsList={addBlacklistDialog.individualItemsList}
        fileColumnSelection={addBlacklistDialog.fileColumnSelection}
        blacklistItems={addBlacklistDialog.fileItemsList}
        linkedinAccounts={linkedinAccounts}
        selectedLinkedinAccount={addBlacklistDialog.selectedLinkedinAccount}
        companyQuery={addBlacklistDialog.companyQuery}
        companyOptions={addBlacklistDialog.companyOptions}
        selectedCompanies={addBlacklistDialog.selectedCompanies}
        companyLoading={addBlacklistDialog.companyLoading}
        companyIsEmpty={addBlacklistDialog.companyIsEmpty}
        handleBlacklistNameChange={d.handleBlacklistNameChange}
        handleBlacklistTypeChange={d.handleBlacklistTypeChange}
        handleBlacklistUploadVariantChange={d.handleBlacklistUploadVariantChange}
        onFileDrop={d.onFileDrop}
        handleColumnTypeChange={d.handleColumnTypeChange}
        handleHasTableHeadingChange={d.handleHasTableHeadingChange}
        handleAcceptColumns={d.handleAcceptColumns}
        handleResetBlacklistItems={d.handleResetBlacklistItems}
        handleResetFileColumnSelection={d.handleResetFileColumnSelection}
        handleTextFieldChange={d.handleTextFieldChange}
        handleAddIndividualItem={d.handleAddIndividualItem}
        handlePasteIndividualItem={d.handlePasteIndividualItem}
        handleRemoveIndividualItem={d.handleRemoveIndividualItem}
        handleLinkedinAccountChange={d.handleLinkedinAccountChange}
        handleCompanyQueryChange={d.handleCompanyQueryChange}
        searchCompanyKeyUp={d.searchCompanyKeyUp}
        searchCompanyKeyDown={d.searchCompanyKeyDown}
        handleToggleCompany={d.handleToggleCompany}
        handleRemoveCompany={d.handleRemoveCompany}
        handleClose={handleCloseAddBlacklistDialog}
        handleCreateGlobalBlacklist={handleCreateGlobalBlacklist}
        handleUpdateBlacklistItems={handleUpdateBlacklistItems}
      />
      <DeleteBlacklistDialog
        open={deleteBlacklistDialog.open}
        loading={deleteBlacklistDialog.loading}
        blacklist={deleteBlacklistDialog.blacklist}
        handleClose={handleCloseDeleteBlacklistDialog}
        handleDeleteBlacklist={handleDeleteBlacklist}
      />
      <EditBlacklistNameDialog
        open={editBlacklistNameDialog.open}
        loading={editBlacklistNameDialog.loading}
        blacklistName={editBlacklistNameDialog.blacklistName}
        handleEditBlacklistNameChange={handleEditBlacklistNameChange}
        handleClose={handleCloseEditBlacklistNameDialog}
        handleRenameBlacklist={handleRenameBlacklist}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    background: theme.palette.backgrounds.main_new,
    height: 'calc(100vh - 60px - 32px)',
    borderRadius: '6px',
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.down('xl')]: {
      height: `calc((100vh / ${ZoomLevels.XL}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1300)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('lg')]: {
      height: `calc((100vh / ${ZoomLevels.LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1000)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_MD}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('md')]: {
      height: `calc((100vh / ${ZoomLevels.MD}) - 60px - 32px)`,
    },
  },
  tabContainer: {
    padding: '16px 32px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  content: {
    height: '100%',
    padding: '16px 32px',
    overflow: 'auto',
  },
}));

export default GlobalBlacklistOverview;
